import {
  Box,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from "@mui/material";
import { useNotification } from "@refinedev/core";
import { Edit } from "@refinedev/mui";
import { useForm } from "@refinedev/react-hook-form";
import { Controller } from "react-hook-form";
import { userRoleOptions } from "@/interfaces";
import { userDeleteNotifications } from "@/lib/account-deletion";
import {
  DOB_FIELD,
  DOB_REASON_FIELD,
  DateOfBirthFields,
  saveDateOfBirth,
} from "./date-of-birth-field";
import { HANDLE_FIELD, HandleField, handleBlocked, useHandleCheck } from "./handle-field";

export const UserEdit = () => {
  const {
    saveButtonProps,
    control,
    register,
    getValues,
    handleSubmit,
    watch,
    formState: { errors },
    refineCore: { id, query, onFinish },
  } = useForm({});
  const { open } = useNotification();

  const record = query?.data?.data;
  const handleOnFile: string = record?.handle ?? "";
  const handleCheck = useHandleCheck(id, watch(HANDLE_FIELD) ?? "", handleOnFile);
  const dobOnFile = (): string => record?.dateOfBirth ?? "";
  const dobChanged = () => {
    const value: string = getValues(DOB_FIELD) ?? "";
    return value !== "" && value !== dobOnFile();
  };

  // One Save for the whole page. A changed date of birth goes first, through
  // its own endpoint (see date-of-birth-field.tsx); if the server refuses it,
  // nothing else is saved, so the admin fixes it and saves again.
  const onSave = handleSubmit(async (values) => {
    const { [DOB_FIELD]: dateOfBirth, [DOB_REASON_FIELD]: reason, ...rest } = values;
    if (handleBlocked(handleCheck)) {
      open?.({ type: "error", message: "Choose another handle before saving" });
      return;
    }
    // Sent only when it changed: an account from before every account had a
    // handle (no backfill yet) holds none, and an empty one is a 400.
    if ((rest[HANDLE_FIELD] ?? "").trim() === handleOnFile) delete rest[HANDLE_FIELD];
    if (id !== undefined && dobChanged()) {
      try {
        await saveDateOfBirth(id, dateOfBirth, reason ?? "");
      } catch (error) {
        const message =
          error && typeof error === "object" && typeof (error as { message?: unknown }).message === "string"
            ? (error as { message: string }).message
            : "Could not update the date of birth";
        open?.({ type: "error", message: `Date of birth not saved: ${message}` });
        return;
      }
    }
    await onFinish(rest);
  });

  return (
    <Edit
      saveButtonProps={{
        ...saveButtonProps,
        disabled: saveButtonProps.disabled || handleBlocked(handleCheck),
        onClick: (e) => void onSave(e),
      }}
      deleteButtonProps={userDeleteNotifications}
    >
      <Box
        component="form"
        sx={{ display: "flex", flexDirection: "column" }}
        autoComplete="off"
      >
        <TextField
          {...register("email", {
            required: "Email is required",
          })}
          error={!!errors?.email}
          helperText={errors?.email?.message?.toString()}
          margin="normal"
          fullWidth
          InputLabelProps={{ shrink: true }}
          type="email"
          label="Email"
          name="email"
        />

        <TextField
          {...register("name", {
            required: "Name is required",
          })}
          error={!!errors?.name}
          helperText={errors?.name?.message?.toString()}
          margin="normal"
          fullWidth
          InputLabelProps={{ shrink: true }}
          type="text"
          label="Name"
          name="name"
        />

        <HandleField
          register={register}
          check={handleCheck}
          emptyHelp={
            handleOnFile
              ? "How everyone who plays what they publish finds them."
              : "None stored yet: the apps show one made from the name."
          }
        />

        <Controller
          control={control}
          name="role"
          rules={{
            required: "Role is required",
          }}
          render={({ field }) => {
            // The record may carry a role outside the two the panel knows about
            // — the column is free text, so anything a script or a migration
            // wrote is legal. Offering it as an extra item keeps the select
            // showing the row's real value instead of rendering blank and
            // rewriting the account's role the moment someone saves an
            // unrelated field.
            const current = typeof field.value === "string" ? field.value : "";
            const options = userRoleOptions.includes(
              current as (typeof userRoleOptions)[number]
            )
              ? userRoleOptions
              : [...userRoleOptions, ...(current ? [current] : [])];

            return (
              <FormControl margin="normal" fullWidth error={!!errors?.role}>
                <InputLabel id="edit-user-role-label">Role</InputLabel>
                <Select
                  {...field}
                  value={current}
                  labelId="edit-user-role-label"
                  label="Role"
                >
                  {options.map((option) => (
                    <MenuItem key={option} value={option}>
                      {option}
                    </MenuItem>
                  ))}
                </Select>
                <FormHelperText>
                  {errors?.role?.message?.toString() ??
                    "'admin' grants access to this panel."}
                </FormHelperText>
              </FormControl>
            );
          }}
        />

        <TextField
          {...register("credits", {
            required: "Credits is required",
            valueAsNumber: true,
          })}
          error={!!errors?.credits}
          helperText={errors?.credits?.message?.toString()}
          margin="normal"
          fullWidth
          InputLabelProps={{ shrink: true }}
          type="number"
          label="Credits"
          name="credits"
        />

        <DateOfBirthFields
          register={register}
          errors={errors}
          getOnFile={dobOnFile}
          isChanged={dobChanged}
        />
      </Box>
    </Edit>
  );
};
